/**
 * Smart Shopping List Agent
 * Creates intelligent shopping lists with farm routing and seasonal optimization
 */

interface ShoppingItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  category: 'produce' | 'dairy' | 'meat' | 'bakery' | 'pantry' | 'other';
  priority: 'high' | 'medium' | 'low';
  preferredFarm?: string;
  notes?: string;
  seasonal?: boolean;
  estimatedPrice?: number;
}

interface ShoppingList {
  id: string;
  name: string;
  items: ShoppingItem[];
  createdAt: Date;
  scheduledFor?: Date;
  status: 'draft' | 'active' | 'completed';
  totalEstimate: number;
}

interface FarmStop {
  farmId: string;
  farmName: string;
  items: ShoppingItem[];
  estimatedTotal: number;
  distance?: number;
  openHours?: string;
}

interface OptimizedRoute {
  stops: FarmStop[];
  totalDistance: number;
  estimatedTime: number;
  estimatedSavings: number;
  alternativeStops: FarmStop[];
}

class SmartShoppingListAgent {
  private categoryOrder = ['produce', 'dairy', 'meat', 'bakery', 'pantry', 'other'];

  /**
   * Create a new shopping list
   */
  createList(name: string, items: Partial<ShoppingItem>[] = []): ShoppingList {
    const processedItems = items.map((item, index) => ({
      id: `item-${Date.now()}-${index}`,
      name: item.name || '',
      quantity: item.quantity || 1,
      unit: item.unit || 'each',
      category: item.category || 'other',
      priority: item.priority || 'medium',
      preferredFarm: item.preferredFarm,
      notes: item.notes,
      seasonal: item.seasonal,
      estimatedPrice: item.estimatedPrice,
    } as ShoppingItem));

    return {
      id: `list-${Date.now()}`,
      name,
      items: processedItems,
      createdAt: new Date(),
      status: 'draft',
      totalEstimate: this.calculateTotal(processedItems),
    };
  }

  /**
   * Calculate total estimate
   */
  private calculateTotal(items: ShoppingItem[]): number {
    return items.reduce((sum, item) => {
      return sum + (item.estimatedPrice || 0) * item.quantity;
    }, 0);
  }

  /**
   * Add item to list with smart categorization
   */
  addItem(list: ShoppingList, itemName: string, quantity: number = 1): ShoppingList {
    const category = this.categorizeItem(itemName);
    const seasonal = this.isSeasonalItem(itemName);
    const estimatedPrice = this.estimatePrice(itemName);

    const newItem: ShoppingItem = {
      id: `item-${Date.now()}`,
      name: itemName,
      quantity,
      unit: this.suggestUnit(itemName),
      category,
      priority: seasonal ? 'high' : 'medium',
      seasonal,
      estimatedPrice,
    };

    const updatedItems = [...list.items, newItem];

    return {
      ...list,
      items: updatedItems,
      totalEstimate: this.calculateTotal(updatedItems),
    };
  }

  /**
   * Categorize item automatically
   */
  private categorizeItem(itemName: string): ShoppingItem['category'] {
    const categories: { [key: string]: string[] } = {
      produce: ['tomato', 'lettuce', 'carrot', 'apple', 'orange', 'strawberry', 'pepper', 'cucumber', 'basil', 'herb'],
      dairy: ['milk', 'cheese', 'yogurt', 'butter', 'cream', 'egg'],
      meat: ['chicken', 'beef', 'pork', 'fish', 'sausage'],
      bakery: ['bread', 'muffin', 'cookie', 'cake', 'pastry'],
      pantry: ['honey', 'jam', 'sauce', 'oil', 'vinegar'],
    };

    const normalized = itemName.toLowerCase();
    for (const [category, keywords] of Object.entries(categories)) {
      if (keywords.some(k => normalized.includes(k))) {
        return category as ShoppingItem['category'];
      }
    }
    return 'other';
  }

  /**
   * Check if item is seasonal
   */
  private isSeasonalItem(itemName: string): boolean {
    const month = new Date().getMonth() + 1;
    const seasonalItems: { [key: string]: number[] } = {
      strawberry: [2, 3, 4],
      tomato: [5, 6, 7, 8, 9],
      pumpkin: [9, 10, 11],
      citrus: [11, 12, 1, 2],
      watermelon: [6, 7, 8],
    };

    const normalized = itemName.toLowerCase();
    for (const [item, months] of Object.entries(seasonalItems)) {
      if (normalized.includes(item) && months.includes(month)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Estimate price for item
   */
  private estimatePrice(itemName: string): number {
    const prices: { [key: string]: number } = {
      tomato: 3.99,
      lettuce: 2.99,
      carrot: 1.99,
      apple: 1.50,
      orange: 0.75,
      strawberry: 4.99,
      eggs: 5.99,
      milk: 4.99,
      cheese: 6.99,
      bread: 4.99,
      honey: 8.99,
    };

    const normalized = itemName.toLowerCase();
    for (const [item, price] of Object.entries(prices)) {
      if (normalized.includes(item)) {
        return price;
      }
    }
    return 3.99; // Default estimate
  }

  /**
   * Suggest appropriate unit
   */
  private suggestUnit(itemName: string): string {
    const units: { [key: string]: string } = {
      milk: 'gallon',
      eggs: 'dozen',
      honey: 'jar',
      bread: 'loaf',
      lettuce: 'head',
      tomato: 'lb',
      carrot: 'bunch',
      strawberry: 'pint',
    };

    const normalized = itemName.toLowerCase();
    for (const [item, unit] of Object.entries(units)) {
      if (normalized.includes(item)) {
        return unit;
      }
    }
    return 'each';
  }

  /**
   * Organize list by category
   */
  organizeByCategory(list: ShoppingList): { category: string; items: ShoppingItem[] }[] {
    const organized: { [key: string]: ShoppingItem[] } = {};

    list.items.forEach(item => {
      if (!organized[item.category]) {
        organized[item.category] = [];
      }
      organized[item.category].push(item);
    });

    return this.categoryOrder
      .filter(cat => organized[cat]?.length > 0)
      .map(category => ({
        category,
        items: organized[category].sort((a, b) => {
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }),
      }));
  }

  /**
   * Optimize shopping route across farms
   */
  optimizeRoute(
    list: ShoppingList,
    farms: { id: string; name: string; products: string[]; location: { lat: number; lng: number }; hours: string }[],
    userLocation: { lat: number; lng: number }
  ): OptimizedRoute {
    const farmStops: FarmStop[] = [];
    const unassignedItems: ShoppingItem[] = [];

    // Assign items to farms
    list.items.forEach(item => {
      const matchingFarm = farms.find(farm =>
        farm.products.some(p =>
          p.toLowerCase().includes(item.name.toLowerCase()) ||
          item.name.toLowerCase().includes(p.toLowerCase())
        )
      );

      if (matchingFarm) {
        const existingStop = farmStops.find(s => s.farmId === matchingFarm.id);
        if (existingStop) {
          existingStop.items.push(item);
          existingStop.estimatedTotal += (item.estimatedPrice || 0) * item.quantity;
        } else {
          farmStops.push({
            farmId: matchingFarm.id,
            farmName: matchingFarm.name,
            items: [item],
            estimatedTotal: (item.estimatedPrice || 0) * item.quantity,
            distance: this.calculateDistance(userLocation, matchingFarm.location),
            openHours: matchingFarm.hours,
          });
        }
      } else {
        unassignedItems.push(item);
      }
    });

    // Sort stops by distance (greedy nearest neighbor)
    const sortedStops = this.sortByDistance(farmStops, userLocation);

    // Calculate totals
    const totalDistance = sortedStops.reduce((sum, stop) => sum + (stop.distance || 0), 0);
    const estimatedTime = Math.round(totalDistance * 2.5); // ~2.5 min per mile

    return {
      stops: sortedStops,
      totalDistance: Math.round(totalDistance * 10) / 10,
      estimatedTime,
      estimatedSavings: this.calculateSavings(sortedStops),
      alternativeStops: [], // Could suggest alternative farms
    };
  }

  /**
   * Calculate distance between two points
   */
  private calculateDistance(
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const R = 3959; // Earth radius in miles
    const dLat = this.toRad(point2.lat - point1.lat);
    const dLng = this.toRad(point2.lng - point1.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(point1.lat)) *
      Math.cos(this.toRad(point2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  /**
   * Sort stops by distance (nearest first)
   */
  private sortByDistance(stops: FarmStop[], start: { lat: number; lng: number }): FarmStop[] {
    return [...stops].sort((a, b) => (a.distance || 0) - (b.distance || 0));
  }

  /**
   * Calculate estimated savings vs grocery store
   */
  private calculateSavings(stops: FarmStop[]): number {
    const farmTotal = stops.reduce((sum, stop) => sum + stop.estimatedTotal, 0);
    const groceryEstimate = farmTotal * 1.3; // Assume 30% higher at grocery
    return Math.round((groceryEstimate - farmTotal) * 100) / 100;
  }

  /**
   * Generate smart suggestions based on list
   */
  generateSuggestions(list: ShoppingList): {
    pairings: string[];
    seasonal: string[];
    savings: string[];
  } {
    const itemNames = list.items.map(i => i.name.toLowerCase());

    const pairings: string[] = [];
    const seasonal: string[] = [];
    const savings: string[] = [];

    // Suggest pairings
    if (itemNames.some(n => n.includes('tomato'))) {
      if (!itemNames.some(n => n.includes('basil'))) {
        pairings.push('Fresh basil pairs perfectly with tomatoes');
      }
    }

    if (itemNames.some(n => n.includes('strawberry'))) {
      if (!itemNames.some(n => n.includes('cream'))) {
        pairings.push('Add fresh cream for strawberries and cream');
      }
    }

    // Suggest seasonal items
    const month = new Date().getMonth() + 1;
    if (month >= 6 && month <= 8) {
      seasonal.push('Watermelon is at peak season!');
      seasonal.push('Sweet corn is freshest now');
    } else if (month >= 11 || month <= 2) {
      seasonal.push('Citrus fruits are in season');
      seasonal.push('Winter greens are at their best');
    }

    // Savings tips
    if (list.items.length > 5) {
      savings.push('Buy in bulk to save more');
    }

    if (list.items.some(i => i.seasonal)) {
      savings.push('Great choice on seasonal items - best prices!');
    }

    return { pairings, seasonal, savings };
  }

  /**
   * Create list from recipe ingredients
   */
  createFromRecipe(
    recipeName: string,
    ingredients: { name: string; quantity: number; unit: string }[]
  ): ShoppingList {
    const items: ShoppingItem[] = ingredients.map((ing, index) => ({
      id: `item-${Date.now()}-${index}`,
      name: ing.name,
      quantity: ing.quantity,
      unit: ing.unit,
      category: this.categorizeItem(ing.name),
      priority: 'medium' as const,
      seasonal: this.isSeasonalItem(ing.name),
      estimatedPrice: this.estimatePrice(ing.name),
    }));

    return {
      id: `list-${Date.now()}`,
      name: `Ingredients for ${recipeName}`,
      items,
      createdAt: new Date(),
      status: 'draft',
      totalEstimate: this.calculateTotal(items),
    };
  }
}

const smartShoppingListAgent = new SmartShoppingListAgent();
export default smartShoppingListAgent;
